import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { Product } from '@/services/api/contracts/catalog/types';
import { useCategories } from '@/services/api/hooks/catalog';
import { useRegions } from '@/services/api/hooks/region';
import { BasicBetterBest, useBasicBetterBestOptions } from '@/services/catalogue/useBasicBetterBest';
import { GBP } from '@dinero.js/currencies';
import { zodResolver } from '@hookform/resolvers/zod';
import { dinero } from 'dinero.js';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PriceAndMarginForm, PriceAndMarginFormFields } from '../../price/PriceAndMarginForm';
import { EditUploadFormFields } from '../EditProduct';
import { BaseProductProperties, ProductDescription, ProductForm, ProductSidebar } from '../ProductForm';
import { ExtendedBulkProduct } from './steps/ProductReviewGrid';

const BulkUploadEditProductFormFields = EditUploadFormFields.merge(PriceAndMarginFormFields);

type BulkUploadEditProductFormFields = z.infer<typeof BulkUploadEditProductFormFields>; // => { students: string[], id: string }

export const EditProductInformationModal: FC<
  ModalProps<{ productId: string }> & {
    products: Omit<Product, 'id' | 'price' | 'code'>[];
    editProduct: (recipe: (products: Omit<ExtendedBulkProduct, 'id' | 'price' | 'code'>[]) => void) => void;
  }
> = ({ editProduct, products, ...props }) => {
  const EditProductionInformationForm = () => {
    const productId = props.props?.productId;
    if (!productId) throw Error('No productId');

    const product = products.find(p => p.productId === productId) as ExtendedBulkProduct;

    if (!product) throw Error('No product');

    const bbbOptions = useBasicBetterBestOptions();

    const methods = useForm<BulkUploadEditProductFormFields>({
      defaultValues: {
        ...product,
        // @ts-expect-error TODO: fix this
        description: product.description.value,
        propertyBag: product.propertyBag.properties,
        price: dinero({ amount: product.catalogPrice?.price || 0, currency: GBP }),
        modifier: String(product.baseMargin.modifier),
        basicBetterBest: bbbOptions.find(o => o.id === product.propertyBag.basicBetterBest) || bbbOptions[0]
      },
      resolver: zodResolver(BulkUploadEditProductFormFields)
    });

    const [categories] = useCategories();
    const [regions] = useRegions();

    const onSubmit = async (data: BulkUploadEditProductFormFields) => {
      editProduct(draft => {
        const excludedProperties = ['min', 'max', 'modifier', 'price'];
        const index = draft.findIndex(p => p.productId === productId);

        const filteredData = {
          ...Object.fromEntries(Object.entries(data).filter(([key]) => !excludedProperties.includes(key)))
        } as unknown as Product;

        const isMainDevice = categories.find(item => item.id === data.category.id)?.isMainDevice;

        draft[index] = {
          ...filteredData,
          manufacturer: {
            id: data.manufacturer.id,
            name: data.manufacturer.name
          },
          category: {
            id: data.category.id,
            name: data.category.name,
            description: '',
            isMainDevice: Boolean(isMainDevice)
          },
          region: {
            id: data.region.id,
            code: data.region.name,
            name: regions.find(region => region.id === data.region.id)!.name
          },
          propertyBag: {
            properties: data.propertyBag,
            basicBetterBest: data.basicBetterBest.id as BasicBetterBest
          },
          description: {
            value: data.description
          },
          baseMargin: {
            modifier: Number(data.modifier),
            effectiveDate: product.baseMargin.effectiveDate ?? new Date()
          },
          catalogPrice: {
            price: data.price.toJSON().amount,
            effectiveDate: product.catalogPrice.effectiveDate ?? new Date()
          }
        };
        console.log(draft);
      });

      props.close();
    };

    return (
      <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-1 flex-col overflow-auto">
        <ProductForm>
          <div className="min-w-[24rem] flex-1">
            <ProductDescription />
          </div>
          <ProductSidebar>
            <BaseProductProperties />
            <div className="grid grid-cols-2 gap-2">
              <PriceAndMarginForm />
            </div>
          </ProductSidebar>
        </ProductForm>
        <Button className="mt-2 w-fit place-self-end" variant={ButtonVariant.Primary} type="submit">
          Update
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="max-h-screen overflow-y-auto md:max-w-5xl" {...props}>
      <Card className="flex w-full px-8 py-8 ">
        <EditProductionInformationForm key={props.props?.productId} />
      </Card>
    </Modal>
  );
};
